import React from "react";
import Box from '@mui/material/Box';
import {useTranslation} from "react-i18next";
import {Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {Button, CircularProgress, LinearProgress} from "@mui/material";
import Typography from "@mui/material/Typography";
import {APP_NAME} from "../App";
import {useAuth} from "../context/AuthContext";
import {useAppContext} from "../context/AppContext";
import usePageTracking from "../hooks/usePageTracking";
import PageAdminAnalyticsCatalog from "./analyses/admin/pages/PageAdminAnalyticsCatalog";

// Lazy loading : chaque page n'est chargée qu'à la première visite
const PageWelcome                = React.lazy(() => import("./PageWelcome"));
const PageExplore                = React.lazy(() => import("./PageExplore"));
const PageContact                = React.lazy(() => import("./PageContact"));
const PageOrigin                 = React.lazy(() => import("./PageOrigin"));
const PagePersonalImport         = React.lazy(() => import("./PagePersonalImport"));
const PagePersonalFolders        = React.lazy(() => import("./PagePersonalFolders"));
const PagePersonalViews          = React.lazy(() => import("./PagePersonalViews"));
const PagePersonalAnalyses       = React.lazy(() => import("./PagePersonalAnalyses"));
const PageDashboards             = React.lazy(() => import("./PageDashboards"));
const PagePersonalAnalysesView   = React.lazy(() => import("./analyses/PagePersonalAnalysesView"));
const PagePersonalAnalysesCreate = React.lazy(() => import("./analyses/PagePersonalAnalysesCreate"));
const AnalyticsTimingView        = React.lazy(() => import("./analyses/timing/AnalyticsTimingView"));
const PageLogin                  = React.lazy(() => import("./auth/PageLogin"));
const PageUserInfo               = React.lazy(() => import("./PageUserInfo"));
const PageReports                = React.lazy(() => import("./PageReports"));
const PageHowTo                  = React.lazy(() => import("./PageHowTo"));
const PageAdminUsers             = React.lazy(() => import("./PageAdminUsers"));
const PageK8sEngines             = React.lazy(() => import("./analyses/admin/pages/PageK8sEngines"));
const PageHealthEngines          = React.lazy(() => import("./analyses/admin/pages/PageHealthEngines"));
const PageQueues                 = React.lazy(() => import("./analyses/admin/pages/PageQueues"));
const PageModels                 = React.lazy(() => import("./analyses/admin/pages/PageModels"));
const PageEngineDetail           = React.lazy(() => import("./analyses/admin/pages/PageEngineDetail"));
const PageAdminTracking          = React.lazy(() => import("./admin/PageAdminTracking"));
const PageAdminNotifications     = React.lazy(() => import("./admin/PageAdminNotifications"));
const PageMyReports              = React.lazy(() => import("./PageMyReports"));
const PageOntologyManager        = React.lazy(() => import("./PageOntologyManager"));
const OntologyConceptsPage       = React.lazy(() => import("./admin/ontology/ConceptsPage"));
const OntologyColumnProfilesPage = React.lazy(() => import("./admin/ontology/ColumnProfilesPage"));
const OntologyExpandPreviewPage  = React.lazy(() => import("./admin/ontology/ExpandPreviewPage"));
const OntologyOrphansPage        = React.lazy(() => import("./admin/ontology/OrphansPage"));

// ── Helpers config.pages ──────────────────────────────────────────────────────

/**
 * Recherche une entrée dans config.pages par type.
 * Retourne null si config.pages n'est pas défini (rétro-compatibilité).
 */
function findPage(config, type) {
    if (!config?.pages) return null;
    return config.pages.find(p => p.type === type) ?? null;
}

/**
 * Vérifie si un type de page est activé ET accessible pour l'utilisateur courant.
 * - Si config.pages n'est pas défini (ancienne config), toujours vrai (rétro-compatibilité).
 * - Sinon, la page doit exister dans config.pages et respecter auth/roles.
 */
function canShowPage(config, type, user) {
    if (!config?.pages) return true;          // pas de config.pages → ancienne config
    const page = findPage(config, type);
    if (!page) return false;                  // type absent de config.pages
    if (!page.auth) return true;              // page publique
    if (!user) return false;                  // auth requise, pas d'utilisateur
    if (!page.roles || page.roles.length === 0) return true;
    return page.roles.includes(user?.instances?.[APP_NAME]?.role);
}

const PageFallback = () => {
    const {t} = useTranslation();
    return (
        <Box>
            {/* Barre de progression fixée en haut de la fenêtre */}
            <LinearProgress
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                }}
            />
            {/* Indicateur centré avec texte */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60vh",
                    gap: 2,
                    opacity: 0,
                    animation: "fadeIn 0.4s ease-in 0.15s forwards",
                    "@keyframes fadeIn": {from: {opacity: 0}, to: {opacity: 1}},
                }}
            >
                <CircularProgress size={48}/>
                <Typography variant="body2" sx={{
                    color: "text.secondary"
                }}>
                    {t("common.loading", "Chargement…")}
                </Typography>
            </Box>
        </Box>
    );
};

function NoMatch() {
    const location = useLocation();
    const navigate = useNavigate();
    const {t} = useTranslation();
    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
            gap: 2
        }}>
            <Typography variant="h1" sx={{fontWeight: "bold", color: "text.secondary"}}>
                404
            </Typography>
            <Typography variant="h5">
                {t("noroute.title")}
            </Typography>
            <Typography variant="body1" sx={{
                color: "text.secondary"
            }}>
                <code>{location.pathname}</code>
            </Typography>
            <Button variant="contained" onClick={() => navigate("/")}>
                {t("noroute.back")}
            </Button>
        </Box>
    );
}

export default function RouteContainer() {
    const { user } = useAuth();
    const { config, setPage } = useAppContext();

    const isAdmin    = user?.instances?.[APP_NAME]?.role === "ADMIN";
    const isProvider = user?.instances?.[APP_NAME]?.role === "PROVIDER";

    // Tracking automatique de la navigation
    usePageTracking(user);

    return (
        <Box sx={{width: '100%'}}>
            <React.Suspense fallback={<PageFallback/>}>
                <Routes>
                    {/* ── Page d'accueil (toujours disponible) ── */}
                    <Route default path="/" element={<PageWelcome/>}/>

                    {/* ── Connexion (toujours disponible) ── */}
                    <Route path="private/login" element={<PageLogin setPage={setPage}/>}/>

                    {/* ── Rapports ── */}
                    {canShowPage(config, "reports", user) && (
                        <Route path="reports/:id" element={<PageReports setPage={setPage} config={config}/>}/>
                    )}
                    {canShowPage(config, "reports", user) && (
                        <Route path="reports/:id/:year" element={<PageReports setPage={setPage} config={config}/>}/>
                    )}

                    {/* ── Tableaux de bord ── */}
                    {canShowPage(config, "dashboards", user) && (
                        <Route path="dashboards" element={<PageDashboards setPage={setPage} config={config} user={user}/>}/>
                    )}
                    {canShowPage(config, "dashboards", user) && (
                        <Route path="dashboards/:id" element={<PageDashboards setPage={setPage} config={config} user={user}/>}/>
                    )}
                    {canShowPage(config, "dashboards", user) && (
                        <Route path="dashboards/:id/:page" element={<PageDashboards setPage={setPage} config={config} user={user}/>}/>
                    )}

                    {/* ── Exploration ── */}
                    {canShowPage(config, "explore", user) && (
                        <Route path="explore" element={<PageExplore setPage={setPage} config={config.explore}/>}/>
                    )}
                    {canShowPage(config, "explore", user) && (
                        <Route path="explore/:id" element={<PageExplore setPage={setPage} config={config.explore}/>}/>
                    )}

                    {/* ── Origines des données ── */}
                    {canShowPage(config, "origins", user) && (
                        <Route path="origin" element={<PageOrigin setPage={setPage} config={config}/>}/>
                    )}
                    {canShowPage(config, "origins", user) && (
                        <Route path="origin/:id" element={<PageOrigin setPage={setPage} config={config}/>}/>
                    )}

                    {/* ── Manuel / Aide ── */}
                    {canShowPage(config, "manuel", user) && (
                        <Route path="howto" element={<PageHowTo setPage={setPage} config={config}/>}/>
                    )}

                    {/* ── Contact ── */}
                    {canShowPage(config, "contact", user) && (
                        <Route path="contact" element={<PageContact setPage={setPage} config={config}/>}/>
                    )}

                    {/* ── Espace personnel (authentifié) ── */}
                    {user && (
                        <Route path="private/info" element={<PageUserInfo user={user} config={config}/>}/>
                    )}

                    {/* ── Vues personnelles ── */}
                    {user && canShowPage(config, "views", user) && (
                        <Route path="private/folders"
                               element={<PagePersonalFolders setPage={setPage} user={user} config={config}/>}/>
                    )}
                    {user && canShowPage(config, "views", user) && (
                        <Route path="private/folders/:id"
                               element={<PagePersonalFolders setPage={setPage} user={user} config={config}/>}/>
                    )}
                    {user && canShowPage(config, "views", user) && (
                        <Route path="private/views/:id"
                               element={<PagePersonalViews setPage={setPage} user={user} config={config}/>}/>
                    )}

                    {/* ── Analyses personnelles ── */}
                    {user && canShowPage(config, "analytics", user) && (
                        <Route path="private/analyses"
                               element={<PagePersonalAnalyses setPage={setPage} user={user} config={config}/>}/>
                    )}
                    {user && canShowPage(config, "analytics", user) && (
                        <Route path="private/analyses/create"
                               element={<PagePersonalAnalysesCreate setPage={setPage} config={config}/>}/>
                    )}
                    {user && canShowPage(config, "analytics", user) && (
                        <Route path="private/analyse/:id"
                               element={<PagePersonalAnalysesView setPage={setPage} config={config}/>}/>
                    )}
                    {user && canShowPage(config, "analytics", user) && (
                        <Route path="private/analyse/:id/timing"
                               element={<AnalyticsTimingView/>}/>
                    )}

                    {/* ── Import de données (ADMIN ou PROVIDER) ── */}
                    {user && (isAdmin || isProvider) && canShowPage(config, "imports", user) && (
                        <Route path="private/import"
                               element={<PagePersonalImport user={user} setPage={setPage} config={config}/>}/>
                    )}

                    {/* ── Pages d'administration (ADMIN uniquement) ── */}
                    {user && isAdmin && (
                        <Route path="admin/users" element={<PageAdminUsers/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/analytics/collection" element={<PageAdminAnalyticsCatalog/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/analytics/k8s" element={<PageK8sEngines/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/analytics/health" element={<PageHealthEngines/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/analytics/queues" element={<PageQueues/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/analytics/models" element={<PageModels/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/analytics/engine/:modelId" element={<PageEngineDetail/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/tracking" element={<PageAdminTracking/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/notifications" element={<PageAdminNotifications/>}/>
                    )}
                    {user && isAdmin && (
                        <Route path="admin/ontology" element={<PageOntologyManager/>}>
                            <Route path="concepts" element={<OntologyConceptsPage/>}/>
                            <Route path="profiles" element={<OntologyColumnProfilesPage/>}/>
                            <Route path="expand" element={<OntologyExpandPreviewPage/>}/>
                            <Route path="orphans" element={<OntologyOrphansPage/>}/>
                        </Route>
                    )}

                    {/* ── Suivi de ses propres signalements (tout utilisateur connecté) ── */}
                    {user && (
                        <Route path="my-reports" element={<PageMyReports/>}/>
                    )}
                    {/* ── Fallback 404 ── */}
                    <Route path="*" element={<NoMatch/>}/>
                </Routes>
            </React.Suspense>
        </Box>
    );
}
